// src/components/PageSEO.js - Per-page SEO/structured data using react-helmet-async
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SITE_CONFIG } from '../config';
import { SEO_CONFIG } from '../config/seo';

const ORIGIN = `https://${SITE_CONFIG.BASE_URL}`;
const AGENT_NAME = SEO_CONFIG.AGENT_NAME;

export default function PageSEO({ title, description, path, type = 'WebPage', jsonLd, noindex }) {
  const canonical = `${ORIGIN}${path || ''}`;
  const image = SEO_CONFIG.OG_IMAGE;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content={type === 'Article' || type === 'BlogPosting' ? 'article' : 'website'} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content={`${AGENT_NAME} Real Estate`} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD */}
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}

// Shared JSON-LD for the RealEstateAgent entity (reusable across pages)
export function agentJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'RealEstateAgent',
    name: AGENT_NAME,
    url: ORIGIN,
    telephone: SEO_CONFIG.AGENT_PHONE,
    email: SEO_CONFIG.AGENT_EMAIL,
    image: SEO_CONFIG.OG_IMAGE,
    address: {
      '@type': 'PostalAddress',
      streetAddress: SEO_CONFIG.STREET_ADDRESS,
      addressLocality: SEO_CONFIG.CITY,
      addressRegion: SEO_CONFIG.STATE,
      postalCode: SEO_CONFIG.ZIP_CODE,
      addressCountry: SEO_CONFIG.COUNTRY
    },
    areaServed: [
      { '@type': 'City', name: 'Flint' },
      { '@type': 'City', name: 'Grand Blanc' },
      { '@type': 'City', name: 'Davison' },
      { '@type': 'City', name: 'Burton' },
      { '@type': 'City', name: 'Swartz Creek' },
      { '@type': 'City', name: 'Oakland County' }
    ],
    ...(SEO_CONFIG.FACEBOOK_URL ? { sameAs: [SEO_CONFIG.FACEBOOK_URL] } : {})
  };
}

export function breadcrumbJsonLd(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: `${ORIGIN}${item.path}`
    }))
  };
}
